import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { FileUtilService } from '@/common/utils/file-util.service';
import { SurveyViewsBaseComponent } from '@/surveys/components/survey-views-base/survey-views-base.component';
import { SurveyViewsBaseService } from '@/surveys/services/survey-views-base.service';
import { AddSurveyDialogService } from '@/surveys/services/add-survey-dialog.service';
import { EnableCookiesDialogService } from '@/surveys/services/enable-cookies-dialog.service';
import { NotificationOptDialogService } from '@/surveys/services/notification-opt-dialog.service';
import { NotificationOptDialogHttpService } from '@/surveys/services/notification-opt-dialog-http.service';
import { WelcomeSurveyDialogService } from '@/surveys/services/welcome-survey-dialog.service';
import { WelcomeSurveyDialogData } from '@/surveys/dialogs-data/welcome-survey-dialog-data';
import { EventService } from '@/surveys/services/event.service';
import { Component, OnDestroy, OnInit, OnChanges, SimpleChanges, Input, inject, signal, computed, DestroyRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppSettings, AppSettingsService } from '@/common/services/app-settings.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { GridViewInstanceComponent } from './grid-view-instance/grid-view-instance.component';
import { CanisterComponent } from './canister/canister.component';

@Component({
    selector: 'app-grid-view',
    templateUrl: './grid-view.component.html',
    styleUrls: ['./grid-view.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatInputModule,
        MatTabsModule,
        MatIconModule,
        MatListModule,
        MatButtonModule,
        MatCardModule,
        MatTooltipModule,
        MatDividerModule,
        GridViewInstanceComponent,
        CanisterComponent
    ]
})
export class GridViewComponent extends SurveyViewsBaseComponent implements OnInit, OnDestroy, OnChanges {
    // Services using inject()
    private appSettingsService = inject(AppSettingsService);
    protected destroyRef = inject(DestroyRef);
    protected eventService = inject(EventService);
    protected paradataEventService = inject(ParadataEventService);
    protected enableCookiesDialogService = inject(EnableCookiesDialogService);

    // Signals for reactive state
    private _settings = signal<AppSettings>({ 
        pegaIntegration: true
    });
    private _surveys = signal<any[]>([]);
    private _active = signal<boolean>(false);
    private _overdueMessage = signal<string>('');
    private _addSurveyText = signal<string>('');

    // Computed signals
    currentSettings = computed(() => this._settings());
    currentSurveys = computed(() => this._surveys());
    isActive = computed(() => this._active());
    currentOverdueMessage = computed(() => this._overdueMessage());
    currentAddSurveyText = computed(() => this._addSurveyText());

    // Properties that will be set by parent components
    @Input() surveys: any[] = [];
    @Input() active: boolean = false;
    @Input('overdue-message') overdueMessage: string = '';
    @Input('add-survey-text') addSurveyText: string = '';

    // Legacy subscriptions for compatibility
    event$: Subscription;

    constructor() {
        super();
        this.event$ = this.eventService.eventListner()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                event => {
                    if (event.tabChanged) {
                        setTimeout(() => {
                            if (this.surveys && Array.isArray(this.surveys)) {
                                this.surveys.sort(this.sortSurveysDefault);
                                this._surveys.set([...this.surveys]);
                            }
                        }, 200);
                    }
                }
            );

        this.appSettingsService.getAppSettings()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe( settings => {
                if ( typeof settings === 'string' ) {
                      // Manually retyping to string then removing tainting and reparsing
                    this._settings.set(JSON.parse( JSON.stringify( settings ).slice( 8, -1 ).replace( /\\/g, '' ) ));
                }
            });
    }

    ngOnInit(): void {
        if (!navigator.cookieEnabled) {
            this.enableCookiesDialogService.open(this.enableCookiesInfo);
        }
        this.updateSurveys();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['surveys'] && changes['surveys'].currentValue) {
            this.updateSurveys();
        }
        if (changes['active'] && changes['active'].currentValue !== undefined) {
            this._active.set(this.active);
        }
        if (changes['overdueMessage'] && changes['overdueMessage'].currentValue !== undefined) {
            this._overdueMessage.set(this.overdueMessage);
        }
        if (changes['addSurveyText'] && changes['addSurveyText'].currentValue !== undefined) {
            this._addSurveyText.set(this.addSurveyText);
        }
    }

    private updateSurveys(): void {
        if (this.surveys && Array.isArray(this.surveys)) {
            this.surveys.sort(this.sortSurveysDefault);
            this._surveys.set([...this.surveys]);
        }
    }

    ngOnDestroy() {
        this.paradataEventService.flushParadataevents();
        // Subscriptions are now handled by takeUntilDestroyed, no manual unsubscribe needed here.
        super.destroy();
    }
}
